// IconButton — icon-only action button per COMPONENTS §Catalog.
//
// Required `aria-label` (TypeScript-enforced) per WCAG 2.2 SC 4.1.2.
// Hit target ≥24×24 enforced via min-w + min-h + p-2.
// Same focus ring + variant system as Button minus text-specific padding.

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type IconButtonVariant = 'primary' | 'secondary' | 'ghost' | 'stage';

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'aria-label'> {
  /** Required — icon-only buttons have no visible text for screen readers. */
  'aria-label': string;
  variant?: IconButtonVariant;
  type?: 'button' | 'submit' | 'reset';
  children: ReactNode;
}

const base =
  'inline-flex items-center justify-center p-2 min-w-[2.5rem] min-h-[2.5rem] rounded-md ' +
  'transition-colors outline-none ' +
  'focus-ring ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const variants: Record<IconButtonVariant, string> = {
  primary:
    'bg-[var(--color-primary-display)] text-[var(--color-on-primary-display)] hover:brightness-110 active:brightness-95',
  secondary:
    'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] border border-[var(--color-border)] ' +
    'hover:bg-[var(--color-bg-hover)] active:bg-[var(--color-border)]',
  ghost:
    'bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] active:bg-[var(--color-border)]',
  // On the STAGE ground (`--color-bg-stage`, dark in both themes): the theme's
  // text tokens would vanish there in light mode — this variant reads the
  // stage's own ink (DESIGN-TOKENS §Color — the stage tokens are theme-invariant).
  stage:
    'bg-[var(--color-on-stage)]/10 text-[var(--color-on-stage)] hover:bg-[var(--color-on-stage)]/20 active:bg-[var(--color-on-stage)]/30',
};

// forwardRef so it can be a React Aria interaction trigger (Tooltip/Menu via
// <Focusable>, which needs the underlying DOM ref).
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { variant = 'ghost', type = 'button', className = '', children, ...props },
  ref,
) {
  return (
    <button ref={ref} type={type} className={`${base} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
});
